import { z } from 'zod';
import { BuildingTypeSchema } from '@/IsekaiRPG/modules/city/cityTypes';

// ============================================
// 角色世界活动规则
// ============================================

export const CharacterZoneKindSchema = z.enum(['city', 'site', 'settlement']);
export type CharacterZoneKind = z.infer<typeof CharacterZoneKindSchema>;

export const CharacterPresenceScopeSchema = z.enum(['building', 'zone', 'nearby', 'state', 'cross_state', 'global']);
export type CharacterPresenceScope = z.infer<typeof CharacterPresenceScopeSchema>;

export const CharacterSceneRoleSchema = z.enum(['owner', 'staff', 'guest']);
export type CharacterSceneRole = z.infer<typeof CharacterSceneRoleSchema>;

export const CharacterPresenceRoleSchema = z.enum(['owner', 'staff', 'guard', 'worker', 'regular', 'visitor']);
export type CharacterPresenceRole = z.infer<typeof CharacterPresenceRoleSchema>;

export const CharacterActivityKindSchema = z.enum([
  'idle',
  'work',
  'rest',
  'meal',
  'social',
  'trade',
  'pray',
  'patrol',
  'study',
  'travel',
  'visit',
]);
export type CharacterActivityKind = z.infer<typeof CharacterActivityKindSchema>;

export const CharacterPresenceFlexibilitySchema = z.enum(['strict', 'normal', 'loose']);
export type CharacterPresenceFlexibility = z.infer<typeof CharacterPresenceFlexibilitySchema>;

export const CharacterTravelPreferenceSchema = z.enum(['avoid', 'normal', 'frequent']);
export type CharacterTravelPreference = z.infer<typeof CharacterTravelPreferenceSchema>;

export const CharacterPlaceKindSchema = z.enum(['home', 'workplace', 'frequent', 'avoid']);
export type CharacterPlaceKind = z.infer<typeof CharacterPlaceKindSchema>;

export const CharacterZoneAnchorRefSchema = z.object({
  /** 该锚点所属的局部活动空间，可指城市、据点或未来的生成聚落 */
  zoneId: z.string().default(''),
  /** zone 的语义类型，便于未来区分城市、探索据点与生成聚落 */
  zoneKind: CharacterZoneKindSchema.default('city'),
  /** 建筑类型锚点，适合 AI 生成与系统后续绑定真实建筑 */
  buildingType: BuildingTypeSchema.optional(),
  /** 精确建筑实例，仅在系统已绑定真实建筑时使用 */
  buildingId: z.string().default(''),
  /** 街道、住宅或自定义活动锚点 */
  anchorId: z.string().default(''),
});
export type CharacterZoneAnchorRef = z.infer<typeof CharacterZoneAnchorRefSchema>;

export const DEFAULT_CHARACTER_ZONE_ANCHOR_REF: CharacterZoneAnchorRef = {
  zoneId: '',
  zoneKind: 'city',
  buildingId: '',
  anchorId: '',
};

export const CharacterActivityTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('home'),
  }),
  z.object({
    kind: z.literal('activityCenter'),
  }),
  z.object({
    kind: z.literal('buildingType'),
    zoneId: z.string().default(''),
    zoneKind: CharacterZoneKindSchema.default('city'),
    buildingType: BuildingTypeSchema,
  }),
  z.object({
    kind: z.literal('buildingId'),
    zoneId: z.string().default(''),
    zoneKind: CharacterZoneKindSchema.default('city'),
    buildingId: z.string().min(1),
  }),
  z.object({
    kind: z.literal('cityExit'),
    zoneId: z.string().default(''),
    zoneKind: CharacterZoneKindSchema.default('city'),
  }),
  z.object({
    kind: z.literal('streetAnchor'),
    zoneId: z.string().default(''),
    zoneKind: CharacterZoneKindSchema.default('city'),
    anchorId: z.string().default(''),
  }),
  z.object({
    kind: z.literal('character'),
    characterId: z.string().min(1),
  }),
  z.object({
    kind: z.literal('worldSite'),
    siteId: z.string().min(1),
  }),
]);
export type CharacterActivityTarget = z.infer<typeof CharacterActivityTargetSchema>;

export const CharacterActivityPreferenceSchema = z.object({
  activity: CharacterActivityKindSchema,
  weight: z.number().min(0).default(1),
  reason: z.string().default(''),
});
export type CharacterActivityPreference = z.infer<typeof CharacterActivityPreferenceSchema>;

export const CharacterPresencePlaceSchema = z.object({
  id: z.string().min(1),
  kind: CharacterPlaceKindSchema.default('frequent'),
  ref: CharacterZoneAnchorRefSchema,
  weight: z.number().min(0).default(1),
  reason: z.string().default(''),
});
export type CharacterPresencePlace = z.infer<typeof CharacterPresencePlaceSchema>;

export const CharacterPresenceCommitmentSchema = z.object({
  /** 稳定 ID，用于运行时 goal 复用与调试 */
  id: z.string().min(1),
  /** 这段时间人物倾向或必须执行的活动 */
  activity: CharacterActivityKindSchema,
  /** 活动的语义目标，系统会在运行时解析为真实 zone / 建筑 / 路径 */
  target: CharacterActivityTargetSchema,
  /** 开始小时（含） */
  startHour: z.number().int().min(0).max(23).default(0),
  /** 结束小时（不含，24 表示跨至当日结束） */
  endHour: z.number().int().min(1).max(24).default(24),
  /** 同时间段内的选择优先级 */
  priority: z.number().int().default(0),
  /** 该活动被剧情、需求或随机事件打断的难度 */
  flexibility: CharacterPresenceFlexibilitySchema.default('normal'),
  /** 在建筑场景中呈现的身份；不定义时使用 presence.scene.defaultRole */
  sceneRole: CharacterSceneRoleSchema.optional(),
  /** 供 AI 生成和调试使用，不直接展示给玩家 */
  reason: z.string().default(''),
});
export type CharacterPresenceCommitment = z.infer<typeof CharacterPresenceCommitmentSchema>;

export const CharacterPresenceRelationshipSchema = z.object({
  id: z.string().min(1),
  zoneId: z.string().default(''),
  zoneKind: CharacterZoneKindSchema.default('city'),
  buildingType: BuildingTypeSchema.optional(),
  buildingId: z.string().default(''),
  role: CharacterPresenceRoleSchema.default('regular'),
  priority: z.number().int().default(0),
  active: z.boolean().default(true),
  reason: z.string().default(''),
});
export type CharacterPresenceRelationship = z.infer<typeof CharacterPresenceRelationshipSchema>;

export const CharacterPresenceMobilitySchema = z.object({
  scope: CharacterPresenceScopeSchema.default('zone'),
  canLeaveCity: z.boolean().default(false),
  maxTravelDistance: z.number().nonnegative().optional(),
  travelPreference: CharacterTravelPreferenceSchema.default('normal'),
  nearbyZoneCount: z.number().int().min(1).optional(),
  crossStateDepth: z.number().int().min(-1).optional(),
});
export type CharacterPresenceMobility = z.infer<typeof CharacterPresenceMobilitySchema>;

export const CharacterPresencePreferencesSchema = z.object({
  preferredActivities: z.array(CharacterActivityPreferenceSchema).default([]),
  avoidedActivities: z.array(CharacterActivityPreferenceSchema).default([]),
  preferredBuildingTypes: z.array(BuildingTypeSchema).default([]),
  avoidedBuildingTypes: z.array(BuildingTypeSchema).default([]),
});
export type CharacterPresencePreferences = z.infer<typeof CharacterPresencePreferencesSchema>;

export const CharacterPresenceConstraintsSchema = z.object({
  available: z.boolean().default(true),
  fixedResident: z.boolean().default(false),
  avoidNightTravel: z.boolean().default(false),
  avoidDangerousAreas: z.boolean().default(true),
});
export type CharacterPresenceConstraints = z.infer<typeof CharacterPresenceConstraintsSchema>;

export const CharacterPresenceSceneSchema = z.object({
  defaultRole: CharacterSceneRoleSchema.default('guest'),
});
export type CharacterPresenceScene = z.infer<typeof CharacterPresenceSceneSchema>;

export const CharacterPresenceSchema = z.object({
  /** 角色的居住锚点。空值表示没有稳定住所，系统会按当前位置或活动中心兜底。 */
  home: CharacterZoneAnchorRefSchema.default(() => ({ ...DEFAULT_CHARACTER_ZONE_ANCHOR_REF })),
  /** 角色最常围绕的活动中心，通常由 AI 根据人设生成，再由系统绑定真实空间。 */
  activityCenter: CharacterZoneAnchorRefSchema.default(() => ({ ...DEFAULT_CHARACTER_ZONE_ANCHOR_REF })),
  /** 角色可活动的空间范围与旅行倾向。 */
  mobility: CharacterPresenceMobilitySchema.default({
    scope: 'zone',
    canLeaveCity: false,
    travelPreference: 'normal',
  }),
  /** 有语义的常去/避开地点。 */
  places: z.array(CharacterPresencePlaceSchema).default([]),
  /** 时间窗口内的生活义务与倾向。 */
  commitments: z.array(CharacterPresenceCommitmentSchema).default([]),
  /** 软偏好，用于没有硬性义务时选择合理活动。 */
  preferences: CharacterPresencePreferencesSchema.default({
    preferredActivities: [],
    avoidedActivities: [],
    preferredBuildingTypes: [],
    avoidedBuildingTypes: [],
  }),
  /** 世界活动约束。 */
  constraints: CharacterPresenceConstraintsSchema.default({
    available: true,
    fixedResident: false,
    avoidNightTravel: false,
    avoidDangerousAreas: true,
  }),
  /** 与地点或组织绑定的事实关系，不代表人物本质分类。 */
  relationships: z.array(CharacterPresenceRelationshipSchema).default([]),
  /** 场景呈现相关的默认身份。 */
  scene: CharacterPresenceSceneSchema.default({
    defaultRole: 'guest',
  }),
});

export type CharacterPresence = z.infer<typeof CharacterPresenceSchema>;

export const DEFAULT_CHARACTER_PRESENCE: CharacterPresence = CharacterPresenceSchema.parse({});

export function parseCharacterPresence(input: unknown): CharacterPresence {
  return CharacterPresenceSchema.parse(input);
}
